import React from "react";
import { createRoot } from "react-dom/client";
import PopupWidget, { PopupWidgetProps } from "./components/PopupWidget";
import { PreviewMode } from "./shared/types/popup-types";
import styleText from "./index.css?inline";
import { ShadowDomProvider } from "./shared/context/shadow-dom-context";
import { embedOptionsFromDataset } from "./lib/embed-script";
import { hostZoomFor, matchHostToWindow } from "./lib/host-zoom";

// Widget initialization options
type PopupWidgetOptions = Omit<
  PopupWidgetProps,
  "organizationId" | "popupId"
> & {
  containerId?: string;
  organizationId?: string;
  popupId?: string;
  debug?: boolean;
  previewMode?: PreviewMode;
  shopifyCustomerId?: string;
};

// Default options
const defaultOptions: PopupWidgetOptions = {
  containerId: "react-popup-widget-container",
  debug: false,
};

const HOST_TAG_NAME = "lovable-popup-host";
const HOST_STYLE_ID_SUFFIX = "-lovable-host-style";

const ensureHostStyles = (containerId: string) => {
  const styleId = `${containerId}${HOST_STYLE_ID_SUFFIX}`;
  if (document.getElementById(styleId)) {
    return;
  }

  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = `
    ${HOST_TAG_NAME}#${containerId},
    div#${containerId} {
      all: initial !important;
      contain: layout paint style size !important;
      position: fixed !important;
      inset: 0 !important;
      width: 100vw !important;
      height: 100vh !important;
      z-index: 2147483647 !important;
      display: block !important;
      margin: 0 !important;
      padding: 0 !important;
      border: none !important;
      background: transparent !important;
      isolation: isolate !important;
      pointer-events: none !important;
    }

    ${HOST_TAG_NAME}#${containerId}[data-popup-active="true"],
    div#${containerId}[data-popup-active="true"] {
      pointer-events: auto !important;
    }
  `;

  document.head.appendChild(style);
};

const applyHostInlineStyles = (host: HTMLElement) => {
  const inlineStyles: Record<string, string> = {
    position: "fixed",
    top: "0",
    left: "0",
    width: "100vw",
    height: "100vh",
    margin: "0",
    padding: "0",
    border: "none",
    background: "transparent",
    display: "block",
    contain: "layout paint style size",
    isolation: "isolate",
    "box-sizing": "border-box",
    "z-index": "2147483647",
    // Cancel a theme's page zoom (lib/host-zoom): under body { zoom: 0.9 }
    // the host was painted at 90% of the window and the backdrop stopped
    // short of the right and bottom edges.
    zoom: hostZoomFor(host.parentElement),
  };

  Object.entries(inlineStyles).forEach(([property, value]) => {
    host.style.setProperty(property, value, "important");
  });
};

// Create Shadow DOM container for complete CSS isolation
const createShadowContainer = (
  containerId: string
): { host: HTMLElement; shadowRoot: ShadowRoot } => {
  ensureHostStyles(containerId);

  let host = document.getElementById(containerId);

  if (host && host.tagName.toLowerCase() !== HOST_TAG_NAME) {
    const replacement = document.createElement(HOST_TAG_NAME);
    replacement.id = containerId;
    replacement.dataset.popupActive = "false";
    host.replaceWith(replacement);
    host = replacement;
  }

  if (!host) {
    host = document.createElement(HOST_TAG_NAME);
    host.id = containerId;
    host.dataset.popupActive = "false";
    document.body.appendChild(host);
  }

  host.className = "";
  applyHostInlineStyles(host);

  const hostWithObserver = host as HTMLElement & {
    __lovableIsolationObserver?: MutationObserver;
    __lovableZoomListener?: () => void;
  };

  if (!hostWithObserver.__lovableIsolationObserver) {
    const observer = new MutationObserver((mutations) => {
      let shouldReapplyStyles = false;

      mutations.forEach((mutation) => {
        if (mutation.type === "attributes") {
          if (mutation.attributeName === "style") {
            shouldReapplyStyles = true;
          }
          if (mutation.attributeName === "class" && host.className !== "") {
            host.className = "";
          }
        }
      });

      if (shouldReapplyStyles) {
        applyHostInlineStyles(host);
      }
    });

    observer.observe(host, {
      attributes: true,
      attributeFilter: ["style", "class"],
    });

    hostWithObserver.__lovableIsolationObserver = observer;
  }

  // A theme can change its page zoom at a breakpoint.
  if (!hostWithObserver.__lovableZoomListener) {
    const zoomedHost = hostWithObserver;
    const onResize = () => matchHostToWindow(zoomedHost);
    window.addEventListener("resize", onResize);
    hostWithObserver.__lovableZoomListener = onResize;
  }

  if (!host.dataset.popupActive) {
    host.dataset.popupActive = "false";
  }
  host.setAttribute("data-lovable-popup-host", "true");

  let shadowRoot = host.shadowRoot;
  if (!shadowRoot) {
    shadowRoot = host.attachShadow({ mode: "open" });

    const fontLink = document.createElement("link");
    fontLink.rel = "stylesheet";
    fontLink.href =
      "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";
    shadowRoot.appendChild(fontLink);

    // Inject styles into shadow DOM with CSS variables and comprehensive resets
    const style = document.createElement("style");
    style.textContent = `
      :host {
        all: initial;
        contain: layout paint style;
        color-scheme: light;
        font-synthesis: none;
        
        /* CSS Variables from index.css - CRITICAL for Tailwind utilities */
        --background: 0 0% 100%;
        --foreground: 222.2 84% 4.9%;
        --card: 0 0% 100%;
        --card-foreground: 222.2 84% 4.9%;
        --popover: 0 0% 100%;
        --popover-foreground: 222.2 84% 4.9%;
        --primary: 222.2 47.4% 11.2%;
        --primary-foreground: 210 40% 98%;
        --secondary: 210 40% 96.1%;
        --secondary-foreground: 222.2 47.4% 11.2%;
        --muted: 210 40% 96.1%;
        --muted-foreground: 215.4 16.3% 46.9%;
        --accent: 210 40% 96.1%;
        --accent-foreground: 222.2 47.4% 11.2%;
        --destructive: 0 84.2% 60.2%;
        --destructive-foreground: 210 40% 98%;
        --border: 214.3 31.8% 91.4%;
        --input: 214.3 31.8% 91.4%;
        --ring: 174 72% 56%;
        --radius: 0.5rem;
      }
      
      /* A more robust CSS reset */
      *,
      *::before,
      *::after {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
        border: 0;
        font-size: 100%;
        font: inherit;
        vertical-align: baseline;
      }
      
      /* HTML5 display-role reset for older browsers */
      article, aside, details, figcaption, figure, 
      footer, header, hgroup, menu, nav, section {
        display: block;
      }
      
      body {
        line-height: 1;
      }
      
      ol, ul {
        list-style: none;
      }
      
      blockquote, q {
        quotes: none;
      }
      
      blockquote:before, blockquote:after,
      q:before, q:after {
        content: '';
        content: none;
      }
      
      table {
        border-collapse: collapse;
        border-spacing: 0;
      }
      
      /* Re-apply some base styles.
         :host ONLY — a universal * rule would apply Inter DIRECTLY to every
         element, beating inheritance and silently overriding the popup's
         configured theme/element fonts (set inline on the panel). Scoped to
         the host, Inter is just the default the tree inherits until the
         panel's own font-family takes over. */
      :host {
        font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }

      /* Rich-text semantics the reset above nukes (font: inherit) — the
         editor's copy fields emit b/i/u/em/strong inline HTML. */
      b, strong { font-weight: 700; }
      i, em { font-style: italic; }
      u { text-decoration: underline; }

      button {
        background: transparent;
        border: none;
        padding: 0;
        margin: 0;
        cursor: pointer;
      }

      button:focus {
        outline: none;
      }

      input, textarea, select {
        font-family: inherit;
        font-size: inherit;
        line-height: inherit;
        color: inherit;
        border: 1px solid grey; /* A default for visibility */
      }

      #lovable-popup-wrapper {
        all: revert;
      }
      
      /* 
       * CRITICAL: Override rem-based Tailwind spacing with fixed pixel values
       * This ensures consistent rendering regardless of the parent page's root font-size.
       * Many Shopify themes use font-size: 62.5% (10px) on :root which breaks rem calculations.
       */
      
      /* Spacing overrides - convert rem to fixed px for layout-critical elements */
      .p-1 { padding: 4px !important; }
      .p-2 { padding: 8px !important; }
      .p-3 { padding: 12px !important; }
      .p-4 { padding: 16px !important; }
      .p-5 { padding: 20px !important; }
      .p-6 { padding: 24px !important; }
      .p-8 { padding: 32px !important; }
      
      .px-1 { padding-left: 4px !important; padding-right: 4px !important; }
      .px-2 { padding-left: 8px !important; padding-right: 8px !important; }
      .px-3 { padding-left: 12px !important; padding-right: 12px !important; }
      .px-4 { padding-left: 16px !important; padding-right: 16px !important; }
      .px-5 { padding-left: 20px !important; padding-right: 20px !important; }
      .px-6 { padding-left: 24px !important; padding-right: 24px !important; }
      .px-8 { padding-left: 32px !important; padding-right: 32px !important; }
      
      .py-1 { padding-top: 4px !important; padding-bottom: 4px !important; }
      .py-2 { padding-top: 8px !important; padding-bottom: 8px !important; }
      .py-3 { padding-top: 12px !important; padding-bottom: 12px !important; }
      .py-4 { padding-top: 16px !important; padding-bottom: 16px !important; }
      .py-5 { padding-top: 20px !important; padding-bottom: 20px !important; }
      .py-6 { padding-top: 24px !important; padding-bottom: 24px !important; }
      .py-8 { padding-top: 32px !important; padding-bottom: 32px !important; }
      
      .pt-4 { padding-top: 16px !important; }
      .pb-4 { padding-bottom: 16px !important; }
      
      .m-1 { margin: 4px !important; }
      .m-2 { margin: 8px !important; }
      .m-3 { margin: 12px !important; }
      .m-4 { margin: 16px !important; }
      
      .mx-0 { margin-left: 0 !important; margin-right: 0 !important; }
      .mx-2 { margin-left: 8px !important; margin-right: 8px !important; }
      .mx-4 { margin-left: 16px !important; margin-right: 16px !important; }
      .mx-auto { margin-left: auto !important; margin-right: auto !important; }
      
      .my-2 { margin-top: 8px !important; margin-bottom: 8px !important; }
      .my-4 { margin-top: 16px !important; margin-bottom: 16px !important; }
      
      .mt-2 { margin-top: 8px !important; }
      .mb-2 { margin-bottom: 8px !important; }
      .mb-4 { margin-bottom: 16px !important; }
      .mb-6 { margin-bottom: 24px !important; }
      
      .space-y-1 > :not([hidden]) ~ :not([hidden]) { margin-top: 4px !important; }
      .space-y-2 > :not([hidden]) ~ :not([hidden]) { margin-top: 8px !important; }
      .space-y-3 > :not([hidden]) ~ :not([hidden]) { margin-top: 12px !important; }
      .space-y-4 > :not([hidden]) ~ :not([hidden]) { margin-top: 16px !important; }
      .space-y-5 > :not([hidden]) ~ :not([hidden]) { margin-top: 20px !important; }
      .space-y-6 > :not([hidden]) ~ :not([hidden]) { margin-top: 24px !important; }
      .space-x-2 > :not([hidden]) ~ :not([hidden]) { margin-left: 8px !important; }
      
      .gap-1 { gap: 4px !important; }
      .gap-2 { gap: 8px !important; }
      .gap-3 { gap: 12px !important; }
      .gap-4 { gap: 16px !important; }
      
      /* Text size overrides - also rem-based */
      .text-xs { font-size: 12px !important; line-height: 16px !important; }
      .text-sm { font-size: 14px !important; line-height: 20px !important; }
      .text-base { font-size: 16px !important; line-height: 24px !important; }
      .text-lg { font-size: 18px !important; line-height: 28px !important; }
      .text-xl { font-size: 20px !important; line-height: 28px !important; }
      .text-2xl { font-size: 24px !important; line-height: 32px !important; }
      .text-3xl { font-size: 30px !important; line-height: 36px !important; }
      
      /* Height overrides for form elements */
      .h-5 { height: 20px !important; }
      .h-11 { height: 44px !important; }
      .h-12 { height: 48px !important; }
      .h-14 { height: 56px !important; }
      
      .w-5 { width: 20px !important; }
      .w-10 { width: 40px !important; }
      .w-20 { width: 80px !important; }
      .h-10 { height: 40px !important; }
      .h-20 { height: 80px !important; }
      
      /* Border radius - often rem-based */
      .rounded { border-radius: 4px !important; }
      .rounded-md { border-radius: 6px !important; }
      .rounded-lg { border-radius: 8px !important; }
      .rounded-xl { border-radius: 12px !important; }
      .rounded-2xl { border-radius: 16px !important; }
      .rounded-full { border-radius: 9999px !important; }
      
      /* Position overrides for close button */
      .right-2 { right: 8px !important; }
      .right-3 { right: 12px !important; }
      .top-2 { top: 8px !important; }
      .top-3 { top: 12px !important; }
      
      /* Ring offset - used in focus states */
      .ring-offset-2 { --tw-ring-offset-width: 2px !important; }
      
      /* Responsive overrides for md breakpoint (768px+) */
      @media (min-width: 768px) {
        .md\\:p-6 { padding: 24px !important; }
        .md\\:p-8 { padding: 32px !important; }
        .md\\:px-8 { padding-left: 32px !important; padding-right: 32px !important; }
        .md\\:px-12 { padding-left: 48px !important; padding-right: 48px !important; }
        .md\\:py-6 { padding-top: 24px !important; padding-bottom: 24px !important; }
        .md\\:py-12 { padding-top: 48px !important; padding-bottom: 48px !important; }
        .md\\:mb-6 { margin-bottom: 24px !important; }
        .md\\:space-y-4 > :not([hidden]) ~ :not([hidden]) { margin-top: 16px !important; }
        .md\\:space-y-6 > :not([hidden]) ~ :not([hidden]) { margin-top: 24px !important; }
        .md\\:space-y-8 > :not([hidden]) ~ :not([hidden]) { margin-top: 32px !important; }
        .md\\:text-lg { font-size: 18px !important; line-height: 28px !important; }
        .md\\:text-xl { font-size: 20px !important; line-height: 28px !important; }
        .md\\:text-2xl { font-size: 24px !important; line-height: 32px !important; }
        .md\\:text-3xl { font-size: 30px !important; line-height: 36px !important; }
        .md\\:h-12 { height: 48px !important; }
        .md\\:h-14 { height: 56px !important; }
        .md\\:w-16 { width: 64px !important; }
        .md\\:h-16 { height: 64px !important; }
        .md\\:w-32 { width: 128px !important; }
        .md\\:h-32 { height: 128px !important; }
        .md\\:right-2 { right: 8px !important; }
        .md\\:top-2 { top: 8px !important; }
      }
      
      ${styleText}
    `;
    shadowRoot.appendChild(style);
  }

  return { host, shadowRoot };
};

// ---------- render state (kept so the preview bridge can re-render) ----------

type PopupRoot = ReturnType<typeof createRoot>;

interface WidgetRenderState {
  root: PopupRoot;
  host: HTMLElement;
  shadowRoot: ShadowRoot;
  organizationId: string;
  popupId?: string;
  activeOnStore?: boolean;
  debug?: boolean;
  previewMode?: PreviewMode;
  shopifyCustomerId?: string;
  /** Bumped by open(); used as the PopupWidget key so each call REMOUNTS the
   *  component. Re-rendering alone is not enough — after the first open the
   *  props are identical, the init effect does not re-run, and a popup the
   *  visitor closed would never come back. */
  openNonce?: number;
  /** Set for the lifetime of an open() render; see PopupWidgetProps.onDemand. */
  onDemand?: boolean;
  onUnavailable?: (reason: string) => void;
}

let widgetRenderState: WidgetRenderState | null = null;

const renderPopupWidget = (
  overrideSettings?: import("@/shared/types/popup-types").PopupSettings | null,
  previewModeOverride?: PreviewMode,
  editMode?: boolean
): void => {
  if (!widgetRenderState) return;
  const s = widgetRenderState;
  s.root.render(
    <ShadowDomProvider shadowRoot={s.shadowRoot} host={s.host}>
      <PopupWidget
        key={s.openNonce ?? 0}
        onDemand={s.onDemand}
        onUnavailable={s.onUnavailable}
        organizationId={s.organizationId}
        popupId={s.popupId}
        activeOnStore={s.activeOnStore}
        debug={s.debug}
        previewMode={previewModeOverride ?? s.previewMode}
        shopifyCustomerId={s.shopifyCustomerId}
        overrideSettings={overrideSettings ?? undefined}
        editMode={editMode}
        onImpression={() => {
          if (s.debug) {
            console.log("Popup impression tracked");
          }
        }}
        onSubmission={(data) => {
          if (s.debug) {
            console.log("Popup submission:", data);
          }
        }}
        onClose={() => {
          if (s.debug) {
            console.log("Popup closed");
          }
        }}
      />
    </ShadowDomProvider>
  );
};

// Initialize popup widget with Shadow DOM
const initPopupWidget = async (
  organizationId?: string,
  popupId?: string,
  debug?: boolean,
  previewMode?: PreviewMode,
  shopifyCustomerId?: string,
  activeOnStore?: boolean
): Promise<void> => {
  const { host, shadowRoot } = createShadowContainer(
    defaultOptions.containerId!
  );

  if (!organizationId) {
    console.error(
      "Organization ID is required. Add data-organization-id attribute to the script tag."
    );
    return;
  }

  // popupId is no longer required: the active popup is resolved from the
  // organisation (currently_active_on_store). A legacy data-popup-id is still
  // accepted and passed through, but it does not gate initialisation.

  // Ensure a single React container inside the shadow root
  let reactContainer = shadowRoot.getElementById(
    "lovable-popup-wrapper"
  ) as HTMLElement | null;

  if (!reactContainer) {
    reactContainer = document.createElement("div");
    reactContainer.id = "lovable-popup-wrapper";
    shadowRoot.appendChild(reactContainer);
  }

  // Create React root and render inside shadow DOM
  type ContainerWithRoot = HTMLElement & { __lovableRoot?: PopupRoot };
  const containerWithRoot = reactContainer as ContainerWithRoot;
  const root = containerWithRoot.__lovableRoot ?? createRoot(containerWithRoot);
  containerWithRoot.__lovableRoot = root;

  widgetRenderState = {
    root,
    host,
    shadowRoot,
    organizationId,
    popupId,
    activeOnStore,
    debug,
    previewMode,
    shopifyCustomerId,
  };

  renderPopupWidget();

  // Make options available globally
  window.__POPUP_WIDGET_OPTIONS__ = {
    ...defaultOptions,
    organizationId,
    popupId,
    activeOnStore,
    debug,
    previewMode,
    shopifyCustomerId,
  };
};

/**
 * Open the popup on demand, ignoring every trigger and frequency rule.
 *
 * Built 18 Sep 2026 for the FlowIQ chat widget's `popup` quick action: a
 * merchant wants the signup offer to live INSIDE the chat widget so a visitor
 * who dismissed the popup can still claim it, instead of the popup having to
 * chase them (Lean Living, Thys Meyer: "if they do reject it they can always
 * go to there").
 *
 * It reuses the hosted landing page's forced-show path — same live form, same
 * webhooks, same discount delivery, a real impression — and differs only in
 * the analytics trigger (`on_demand`). Deliberately NOT bypassed: the
 * server-side returning-customer skip. A first-order offer should stay hidden
 * from someone who has already ordered; the promise resolves false and the
 * caller says so.
 *
 * Resolves true when the popup rendered, false when it could not.
 */
const openPopupOnDemand = (): Promise<boolean> => {
  if (!widgetRenderState) {
    console.warn("ReactPopupWidget.open(): widget not initialised yet");
    return Promise.resolve(false);
  }
  const s = widgetRenderState;
  return new Promise<boolean>((resolve) => {
    let settled = false;
    const done = (ok: boolean) => {
      if (settled) return;
      settled = true;
      resolve(ok);
    };
    s.openNonce = (s.openNonce ?? 0) + 1;
    s.onDemand = true;
    s.onUnavailable = (reason: string) => {
      if (s.debug) console.log("ReactPopupWidget.open(): unavailable —", reason);
      done(false);
    };
    renderPopupWidget(null, "page");
    // The component reports failure synchronously-ish through onUnavailable;
    // anything still unsettled by then rendered, so report success. The wait
    // covers the settings fetch.
    window.setTimeout(() => done(true), 4000);
  });
};

// ---------- builder live-preview bridge ----------
//
// The flowIQ builder embeds this widget in an iframe and pushes DRAFT settings
// as the user edits, so the preview updates live without saving or reloading.
// Contract (all messages namespaced, harmless on merchant pages):
//   builder → widget: { type: "flowapt:preview-ping" }
//   widget → builder: { type: "flowapt:preview-ready", version: 1 }
//   builder → widget: { type: "flowapt:preview-settings",
//                       settings: PopupSettings,
//                       previewMode?: "welcome" | "success",
//                       editMode?: boolean }   // drag-resize overlay
//   widget → builder: { type: "flowapt:design-edit", patch: DesignEditPatch }
//                     (posted by the overlay when a drag commits)
if (typeof window !== "undefined") {
  window.addEventListener("message", (event: MessageEvent) => {
    const data = event.data as
      | { type?: string; settings?: unknown; previewMode?: string; editMode?: boolean }
      | undefined;
    if (!data || typeof data.type !== "string") return;

    if (data.type === "flowapt:preview-ping") {
      try {
        (event.source as WindowProxy | null)?.postMessage(
          { type: "flowapt:preview-ready", version: 1 },
          "*"
        );
      } catch {
        /* cross-origin source gone — ignore */
      }
      return;
    }

    if (data.type === "flowapt:preview-settings") {
      if (!widgetRenderState || !data.settings) return;
      // Every editor step previews as ITSELF. This used to collapse anything
      // that was not success/teaser to "welcome", so the intro, deck, scratch
      // and wheel previews all showed the form in bridge mode.
      // Every page the editor's step strip can preview. "question" and "phone"
      // were missing until 7 Oct 2026, so both previewed as the form; the
      // Phase 3 step strip surfaced it.
      const BRIDGE_MODES: ReadonlyArray<PreviewMode> = [
        "intro",
        "scratch",
        "question",
        "welcome",
        "phone",
        "wheel",
        "deck",
        "success",
        "teaser",
      ];
      const mode: PreviewMode = BRIDGE_MODES.includes(data.previewMode as PreviewMode)
        ? (data.previewMode as PreviewMode)
        : "welcome";
      renderPopupWidget(
        data.settings as import("@/shared/types/popup-types").PopupSettings,
        mode,
        data.editMode === true
      );
    }
  });
}

/** Build stamp, bumped on every ship. Read it on a store as
 *  `window.ReactPopupWidget.build` (or grep the bundle for it) to know which
 *  commit the CDN is actually serving — Vercel occasionally skips a push. */
export const WIDGET_BUILD = "2026-10-30.click-and-page-triggers";

// Expose init function to window
declare global {
  interface Window {
    ReactPopupWidget: {
      init: typeof initPopupWidget;
      initPopupWidget: typeof initPopupWidget;
      /** Show the popup now, whatever the triggers say. See openPopupOnDemand. */
      open: typeof openPopupOnDemand;
      build: string;
    };
    __POPUP_WIDGET_OPTIONS__?: PopupWidgetOptions;
  }
}

// Initialize the global object
if (typeof window !== "undefined") {
  window.ReactPopupWidget = {
    init: initPopupWidget,
    initPopupWidget: initPopupWidget,
    open: openPopupOnDemand,
    build: WIDGET_BUILD,
  };
}

// Start on our own when the tag says so: data-organization-id alone is enough
// (the one-tag install); data-auto-init="false" opts out. See embed-script.ts.
if (typeof document !== "undefined") {
  const scriptTag = document.currentScript as HTMLScriptElement | null;
  // Capture data attributes BEFORE calling async init function
  const embed = scriptTag ? embedOptionsFromDataset(scriptTag.dataset) : null;
  if (embed?.start) {
    const { organizationId, popupId, activeOnStore, debug } = embed;
    const previewMode = embed.previewMode as PreviewMode | undefined;
    // Read Shopify customer ID from ShopifyAnalytics JS global (only set for logged-in customers)
    const shopifyCustomerId: string | undefined = (() => {
      try {
        const id = (window as any)?.ShopifyAnalytics?.meta?.page?.customerId;
        return id ? String(id) : undefined;
      } catch {
        return undefined;
      }
    })();

    // Pass captured values to init function
    initPopupWidget(organizationId, popupId, debug, previewMode, shopifyCustomerId, activeOnStore);
  }
}

// Get widget options from global state if available
const getGlobalOptions = (): Partial<PopupWidgetProps> => {
  if (typeof window !== "undefined") {
    const options = window.__POPUP_WIDGET_OPTIONS__ || {};
    return options;
  }
  return {};
};

export default initPopupWidget;
